import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, CheckCircle, Share2, Users, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function ReferralPage() {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateCode = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referrals/create`, { referrer_name: name, referrer_email: email });
      setReferral(res.data);
      toast.success("Referral code generated!");
    } catch { toast.error("Failed to generate code."); } finally { setLoading(false); }
  };

  const shareUrl = referral ? `${window.location.origin}/booking?ref=${referral.code}` : "";

  const copyCode = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); toast.success(t("referral.copied")); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div data-testid="referral-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0} className="text-center mb-12">
              <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6"><Gift size={28} className="text-gold" /></div>
              <p className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">Referral Program</p>
              <h1 className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">{t("referral.title")}</h1>
              <p className="text-white/50 font-body text-base max-w-lg mx-auto">{t("referral.subtitle")}</p>
            </motion.div>

            {/* How it works */}
            <motion.div variants={fadeUp} custom={1} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              {[
                { icon: Gift, title: "Get Your Code", desc: "Enter your details and receive a unique referral code" },
                { icon: Share2, title: "Share It", desc: "Send your link to friends planning luxury events" },
                { icon: DollarSign, title: "Earn $250", desc: "Get $250 off when they book. They save too!" },
              ].map((step, i) => (
                <div key={i} className="glass-card p-6 text-center">
                  <step.icon size={24} className="text-gold mx-auto mb-3" />
                  <p className="text-white font-body text-sm font-medium mb-1">{step.title}</p>
                  <p className="text-white/40 font-body text-xs">{step.desc}</p>
                </div>
              ))}
            </motion.div>

            {!referral ? (
              <motion.form variants={fadeUp} custom={2} onSubmit={generateCode} className="glass-card p-8 space-y-5" data-testid="referral-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">{t("referral.nameLabel")} *</label>
                    <Input data-testid="referral-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">{t("referral.emailLabel")} *</label>
                    <Input data-testid="referral-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="referral-submit" className="w-full bg-gold text-black font-body font-medium text-sm py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
                  {loading ? "Generating..." : t("referral.generate")}
                </button>
              </motion.form>
            ) : (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 text-center" data-testid="referral-result">
                <CheckCircle size={36} className="text-gold mx-auto mb-4" />
                <p className="text-white font-heading text-xl mb-2">Your Referral Code</p>
                <div className="bg-gold/10 border border-gold/30 px-6 py-4 inline-block my-4">
                  <p className="text-gold font-body text-2xl font-bold tracking-wider" data-testid="referral-code">{referral.code}</p>
                </div>
                <p className="text-white/40 font-body text-sm mb-6">{t("referral.shareText")}</p>
                <div className="flex items-center justify-center gap-3">
                  <button onClick={copyCode} data-testid="referral-copy" className="flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 hover:text-gold hover:border-gold/30 px-5 py-3 text-xs font-body transition-colors">
                    {copied ? <CheckCircle size={14} /> : <Copy size={14} />} {copied ? t("referral.copied") : "Copy Link"}
                  </button>
                  <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Use my referral code ${referral.code} to get $250 off at Aristocrats JA! ${shareUrl}`)}`, "_blank")} className="flex items-center gap-2 bg-green-600/20 text-green-400 px-5 py-3 text-xs font-body hover:bg-green-600/30 transition-colors">
                    <Share2 size={14} /> WhatsApp
                  </button>
                </div>
                <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-8 text-sm font-body">
                  <div><span className="text-white/30 text-xs block">{t("referral.earned")}</span><span className="text-gold font-medium">${referral.total_earned || 0}</span></div>
                  <div><span className="text-white/30 text-xs block">Referrals</span><span className="text-white/70 font-medium">{referral.referral_count || 0}</span></div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
